import { RefObject, useEffect } from "react";

// Cursor tracking for the composer aurora. While the pointer is over the
// container it eases two CSS custom properties toward the pointer x:
// --aurora-pointer-x (where the hover "spike" surges up) and --aurora-lean (a
// small sideways offset added to the beam's gradient stops so the whole aurora
// leans toward the cursor). When the pointer leaves it eases them back to centre
// so the always-rendered resting aurora never stays leaning. Pointer-fine devices
// only (touch/mobile skip it) and disabled under reduced motion. Purely
// decorative — it only writes CSS custom properties + one state class, no
// business logic.
// STILLNESS: a resting mouse shouldn't hold the crest up forever — after
// STILL_DELAY_MS without movement the hook adds `--pointer-still` on the
// composer, which gates the :hover magnet rule off (ComposerShell.scss), so
// the crest eases back to the idle beam via the normal magnet transition and
// the colours re-centre. The next movement lifts it straight back up.
// `disabled` (AI-working): the working state owns the crest — a keyframe sweeps
// --aurora-pointer-x and hover must add NOTHING on top — so we detach and drop
// our inline vars (the keyframe would out-cascade the inline pointer-x anyway,
// but a stale inline --aurora-lean would keep the colours following the mouse).
const STILL_CLASS = "chat-message-composer--pointer-still";
const STILL_DELAY_MS = 1200;

const useAuroraPointerFollow = (
  ref: RefObject<HTMLElement | null>,
  disabled = false,
): void => {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    if (disabled) {
      el.style.removeProperty("--aurora-pointer-x");
      el.style.removeProperty("--aurora-lean");
      el.classList.remove(STILL_CLASS);
      return;
    }
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    // Maximum sideways lean of the colours toward the cursor, in %. Independent
    // tuning knob — not required to match any SCSS value.
    const LEAN_AMP = 9;
    let raf = 0;
    let curX = 50;
    let targetX = 50;
    let curLean = 0;
    let stillTimer = 0;

    const tick = () => {
      curX += (targetX - curX) * 0.18;
      const targetLean = ((curX - 50) / 50) * LEAN_AMP;
      curLean += (targetLean - curLean) * 0.18;
      el.style.setProperty("--aurora-pointer-x", `${curX.toFixed(1)}%`);
      el.style.setProperty("--aurora-lean", `${curLean.toFixed(2)}%`);
      raf =
        Math.abs(targetX - curX) > 0.3 || Math.abs(targetLean - curLean) > 0.05
          ? requestAnimationFrame(tick)
          : 0;
    };

    const schedule = () => {
      if (!raf) {
        raf = requestAnimationFrame(tick);
      }
    };

    // The mouse settled: melt the crest back to the idle beam (the class gates
    // the :hover magnet rule off) and ease the colours back to centre, so a
    // resting cursor leaves the aurora looking exactly like no-hover idle.
    const onStill = () => {
      el.classList.add(STILL_CLASS);
      targetX = 50;
      schedule();
    };

    const markMoving = () => {
      if (el.classList.contains(STILL_CLASS)) {
        el.classList.remove(STILL_CLASS);
      }
      window.clearTimeout(stillTimer);
      stillTimer = window.setTimeout(onStill, STILL_DELAY_MS);
    };

    const onPointerMove = (event: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) {
        return;
      }
      const ratio = (event.clientX - rect.left) / rect.width;
      targetX = Math.max(0, Math.min(1, ratio)) * 100;
      markMoving();
      schedule();
    };

    // Arm the stillness timer on enter too: content can scroll UNDER a static
    // cursor (:hover applies without any pointermove), and the crest must not
    // stay up forever in that case either.
    const onPointerEnter = () => {
      markMoving();
    };

    // Ease back to centre when the pointer leaves so the resting aurora doesn't
    // stay leaning at the last cursor position.
    const onPointerLeave = () => {
      window.clearTimeout(stillTimer);
      el.classList.remove(STILL_CLASS);
      targetX = 50;
      schedule();
    };

    el.addEventListener("pointerenter", onPointerEnter);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerleave", onPointerLeave);
    el.addEventListener("pointercancel", onPointerLeave);
    return () => {
      el.removeEventListener("pointerenter", onPointerEnter);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerleave", onPointerLeave);
      el.removeEventListener("pointercancel", onPointerLeave);
      window.clearTimeout(stillTimer);
      el.classList.remove(STILL_CLASS);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [ref, disabled]);
};

export default useAuroraPointerFollow;
